import * as THREE from 'three';

import { getMaxPixelAxialDistance, createLabelMeasurementContext } from '../../../../gui/Label3D';
import LayerBackButton from "../LayerBackButton";
import { FacetSpaces, FacetTypes } from "../../../facets/Facets";

const _tmpMat4 = new THREE.Matrix4();

const LABEL_MAXW_FULL = 300;
const LABEL_MAXW_TRUNCATED = 100; // Same as max-width in SpacesLayer.css

const SPACE_LABEL_FONT = "400 11px ArtifaktElement";

let spaceMeasureCtx;

export class SpaceLabel {
  #availablePx;
  #isFullLabel;

  constructor(space, placement, hud) {
    const { dbId, model } = space;

    const it = model.getInstanceTree();

    this.hud = hud;
    this.name = it.getNodeName(dbId);
    this.placement = placement;
    this.space = space;
    this.bounds = null;
    this.worldBox = null;
    this.viewBox = new THREE.Box2();
    this.isSelected = false;

    if (!spaceMeasureCtx) {
      spaceMeasureCtx = createLabelMeasurementContext(hud.viewer);
      spaceMeasureCtx.font = SPACE_LABEL_FONT;
    }
  }

  dispose() {
    if (!this.label) return;

    this.label.clearListeners();
    this.label.dtor();
    this.label = null;
  }

  isVisible() {
    return Boolean(this.label?.visible);
  }

  isInDOM() {
    return !!this.label;
  }

  hasEnoughSpace(drawRect) {
    if (!this.label) {
      // Label not created yet, guess bounds if it's empty
      this.bounds ??= estimateCanvasTextDimensions(this.name);
    }

    if (this.label && !this.label.isOnScreen(drawRect)) {
      return false;
    }

    // Same check as Label3D.shouldBeHidden
    this.#availablePx = getMaxPixelAxialDistance(this.hud.viewer, this.#getWorldBox(), true);
    const shouldBeHidden = this.#availablePx < this.bounds.width;
    return !shouldBeHidden;
  }

  setVisible(visible) {
    if (!this.label && !visible) {
      return;
    }

    if (!this.label) {
      // Delay label instantiation until asked to be visible.
      this.#makeLabel(this.hud, this.placement.position);
      // Label position may have been initialized without animated transform, update it
      this.updateLabelPosition();
      this.updateRoomName();
    }

    this.label.setVisible(visible);
  }

  setSelected(value, faded) {
    if (!this.label) {
      return;
    }

    if (value !== this.isSelected) {
      if (value) {
        this.label.container.classList.add("hud-room-selected");
        this.backBtn.show();
      } else {
        this.label.container.classList.remove("hud-room-selected");
        this.backBtn.hide();
      }
    }

    if (faded && !value) {
      //this.label.container.classList.add("hud-room-fade");
      this.label.textDiv.classList.add("hud-room-fade");
    } else {
      //this.label.container.classList.remove("hud-room-fade");
      this.label.textDiv.classList.remove("hud-room-fade");
    }

    this.isSelected = value;
  }

  refreshMaxWidth() {
    const fullLabel = this.#availablePx * 2 > this.hud.viewer.canvasWrap.clientWidth;

    if (this.#isFullLabel === undefined || this.#isFullLabel !== fullLabel) {

      this.label.textDiv.style.maxWidth = `${fullLabel ? LABEL_MAXW_FULL : LABEL_MAXW_TRUNCATED}px`;
      this.label.textDiv.textContent = this.name;

      this.#isFullLabel = fullLabel;
    }
  }

  updateRoomName() {
    const { dbId, model } = this.space;
    const it = model.getInstanceTree();

    this.name = it.getNodeName(dbId);
    this.#updateLabelBounds();

    // if needed, update textDiv
    this.label?.update();

    // also update space props
    this.space.name = this.name;
  }

  #updateLabelBounds() {
    if (!this.label) {
      this.bounds = estimateCanvasTextDimensions(this.name);
    } else {
      this.label.textDiv.textContent = this.name;
      this.bounds.height = this.label.textDiv.scrollHeight;
      this.bounds.width = this.label.textDiv.scrollWidth;
      this.label.minPixels = this.bounds.width;
      this.refreshMaxWidth();
    }
  }

  #makeLabel(hud, position) {

    const lbl = hud.addLabel(this.space.model, this.space.dbId, false /* isPin */, { noDefaultStyle: true });
    lbl.setElement(this.space);

    if (!lbl.textDiv) lbl.initTextLabel(); // Labels don't initialize textDiv if not needed. We still want it.
    lbl.container.classList.add('hud-room');

    if (position) {
      lbl.setPosition(position);
    } else {
      console.info('Creating space label without explicit positioning', this.name);
    }

    lbl.worldBox = this.#getWorldBox();

    this.label = lbl;

    this.backBtn = new LayerBackButton(hud.twin.facetsManager, hud.viewer.container.ownerDocument, FacetTypes.spaces, FacetSpaces.makeId(this.space));
    this.backBtn.attachTo(this);
  }

  updateLabelPosition() {
    // No label or position to update
    if (!this.placement.position) return;

    const { dbId, model } = this.space;

    const it = model.getInstanceTree();
    const fl = model.getFragmentList();

    let moved = false;
    it?.enumNodeFragments(
      dbId,
      (fragId) => {
        moved = fl.getAnimTransformMatrix(fragId, _tmpMat4);
        return moved; // Only need the first valid transformation matrix
      }, false);

    this.placement.position.copy(this.placement.defaultPosition);

    if (moved) {
      this.placement.position.applyMatrix4(_tmpMat4);
    }

    this.label?.setPosition(this.placement.position);
  }

  updateWorldBox() {
    if (!this.worldBox) return;
    this.worldBox = getSpaceTopPlane(this.space.model, this.space.dbId);
    if (!this.label) return;
    this.label.worldBox = this.worldBox;
  }

  #getWorldBox() {
    this.worldBox ??= getSpaceTopPlane(this.space.model, this.space.dbId);
    return this.worldBox;
  }

  updateViewBox() {
    const { x, y } = this.label ?
    this.label.getCanvasPosition() :
    this.hud.viewer.impl.worldToClient(this.placement.position);
    const halfWidth = this.bounds.width * 0.5;
    const halfHeight = this.bounds.height * 0.5;

    // Assumes LABEL_ANCHORING.MIDDLE
    this.viewBox.min.set(x - halfWidth, y - halfHeight);
    this.viewBox.max.set(x + halfWidth, y + halfHeight);
  }

}

function getSpaceTopPlane(model, dbId, dstBox = new THREE.Box3()) {
  model.getElementBounds(dbId, dstBox);

  dstBox.min.z = dstBox.max.z; // Force to the top plane

  return dstBox;
}

// Try to guess height and width of a canvas text element
function estimateCanvasTextDimensions(text) {

  const padding = 5; // space between adjacent words, per SpacesLayer.CSS
  const lineHeight = 18; // line height is set in SpacesLayer.css
  const words = text.split(' ');

  // Maximum width defined by 100px, per SpacesLayer.CSS
  let firstLineWidth = 0;
  let wrapped;

  for (let i = 0; i < words.length; i++) {
    const wordMeasure = spaceMeasureCtx.measureText(words[i]);
    const wordWidth = wordMeasure.width + padding;

    if (!wrapped) {
      // Track wrap state
      firstLineWidth += wordWidth;
      wrapped = firstLineWidth > LABEL_MAXW_TRUNCATED;
    } else {
      // First line overflowed, return maximum allowed label size
      return { width: LABEL_MAXW_TRUNCATED, height: lineHeight * 2 };
    }
  }

  // No wrapping
  const maxWidth = Math.min(firstLineWidth, LABEL_MAXW_TRUNCATED); // Max 100px
  const maxHeight = lineHeight; // Max two lines

  return { width: maxWidth, height: maxHeight };
}